// Sentry wiring shared by the two places the job board can fail: the Worker
// that renders every Posting page on demand, and the browser that runs the
// page's islands. Both sides report into one Sentry project, so the options
// that must not drift between them — the DSN, the "errors only" stance and
// the data-collection stance — are declared once, here.
//
// Read at build time by `sentry.client.config.ts` and at Worker start-up by
// `sentry.server.config.ts`. Keep it free of Cloudflare and DOM globals: it is
// bundled into both a Worker and a browser chunk.

/**
 * The Sentry DSN this app reports errors to.
 *
 * A DSN is a public value — it is an ingest endpoint, not a credential, and the
 * browser SDK cannot work without it in the shipped bundle. So it is a constant
 * rather than an environment variable: `import.meta.env` would only move the
 * same string into the same bundle through one more moving part.
 *
 * JOBS-710: the Sentry project was renamed from `jobs` to `proseafarers`. A
 * DSN addresses a project by its numeric ID, not its slug, so this value was
 * unaffected by the rename and needs no change. The slug is what still
 * matters elsewhere: `astro.config.mjs`'s sourcemap-upload `project` and
 * `sentry-cli` in CI both address the project by name, so they read the new
 * slug instead.
 */
export const SENTRY_DSN =
  "https://59e40dd1d3951682965360079b0a31cb@o73733.ingest.us.sentry.io/4511904582074368";

/** Environment name used when nothing supplies one. */
export const SENTRY_DEFAULT_ENVIRONMENT = "development";

/**
 * The Sentry environment name for a run of the app.
 *
 * Sentry defaults an unset environment to `production`, which would file a
 * developer's own broken build against the live job board. Default to
 * `development` instead, so only a config that names an environment can reach
 * `staging` or `production`.
 *
 * The Worker passes `env.SENTRY_ENVIRONMENT` (a wrangler var, one per
 * environment config); the browser passes
 * `import.meta.env.PUBLIC_SENTRY_ENVIRONMENT` (set by the deploy workflow's
 * Build step).
 *
 * @param name The configured name, if any. Blank and whitespace count as unset.
 */
export function sentryEnvironment(name?: string | null): string {
  const trimmed = name?.trim();
  return trimmed ? trimmed : SENTRY_DEFAULT_ENVIRONMENT;
}

/**
 * Whether this run may send events to Sentry at all.
 *
 * **Only a deployed environment reports.** A local run — `pnpm run dev`, an
 * `astro build` on a laptop, and the Playwright acceptance suite, all of which
 * resolve to {@link SENTRY_DEFAULT_ENVIRONMENT} — initialises the SDK with
 * `enabled: false`, so nothing leaves the machine.
 *
 * This deliberately reverses the earlier stance, which tagged local events
 * `development` and shipped them. Tagging kept them out of the production
 * environment view but not out of the project: they still consumed quota, and a
 * browser test suite that asserts an error page would send one event per
 * assertion, every run. The wider point is that a local event carries whatever
 * {@link SENTRY_DATA_COLLECTION} allows — cookies, both header directions, query
 * strings, D1 statement values and stack-frame locals — off a developer's
 * machine, for a crash they are already reading in their own terminal.
 *
 * The cost is real and worth stating: a genuine local failure no longer appears
 * in Sentry. Read it in the terminal, or export `SENTRY_ENVIRONMENT` /
 * `PUBLIC_SENTRY_ENVIRONMENT` with a deployed-looking name for a one-off.
 *
 * @param name The configured environment name, if any.
 */
export function sentryReportingEnabled(name?: string | null): boolean {
  return sentryEnvironment(name) !== SENTRY_DEFAULT_ENVIRONMENT;
}

/**
 * What the SDK may attach to an event.
 *
 * Every field is stated even where it repeats a documented default, because of
 * how v11 resolves this option: each field that `dataCollection` omits takes
 * the SDK's own DEFAULTS, which collect cookies, request and response headers
 * and query strings *unfiltered*. v11 has no `sendDefaultPii` fallback.
 * Anything omitted here is therefore opted in, not opted out.
 *
 * **These values match `manning`'s deliberately** (Sam, 2026-08-14), so the two
 * apps collect the same fields and a reader does not have to hold two stances in
 * mind. Martide has a Data Processing Agreement with Sentry, which is what makes
 * the wider set lawful.
 *
 * This replaced a tighter set. What that set withheld, so a future reader can
 * weigh reversing it: `cookies` and both header directions were `false`, and
 * `urlQueryParams` was an allow list of the board's own structured filters —
 * `rank`, `vessel_type`, `salary_min`, `join_date_from`, `sort` and `page` —
 * which dropped `q` and `location`, the free text a seafarer types into the
 * search box. `databaseQueryData` was `false` for the same reason, because a D1
 * statement value can hold that search term.
 *
 * Two things to know before widening this further or narrowing it again:
 *
 * - The SDK filters sensitive keys regardless of these values. `auth`, `token`,
 *   `secret`, `session`, `cookie` and similar names are replaced in headers and
 *   cookies whatever `cookies` and `httpHeaders` say, so `true` here is not
 *   "send the session".
 * - Unlike `manning`, this repository's privacy copy names processor
 *   *categories* rather than processors, so Sentry is not disclosed by name
 *   there. `manning` names it explicitly. That gap is worth closing separately.
 */
export const SENTRY_DATA_COLLECTION = {
  // The one field held off. It is the only one that adds a new *category* of
  // personal data rather than enriching a stack trace: it derives `user.*` from
  // the request, which on Cloudflare means the visitor's address.
  userInfo: false,

  // Everything below is on. Each is load-bearing when reading a real incident,
  // and the DPA is what makes collecting it lawful.
  cookies: true,
  httpHeaders: { request: true, response: true },
  urlQueryParams: true,
  // D1 statement values and returned rows. This app renders every Posting page
  // from D1 on demand, so a failed statement is the most likely incident here
  // and this is what makes it diagnosable.
  databaseQueryData: true,
  // Local variables at each stack frame. The single most useful field when
  // reading a frame, and stated rather than inherited so the list is complete.
  stackFrameVariables: true,

  // No body, in either direction. Nothing here posts one today — the board's
  // forms are `method="get"` searches and an application happens on the portal —
  // and this keeps that true if something later does. It also matches `manning`,
  // where a request body is an Applicant's CV.
  httpBodies: [],

  // Deliberately not `as const`: the SDK's `DataCollection.httpBodies` is a
  // mutable array, so a readonly literal is rejected at both init sites. Those
  // two sites are what typechecks this object against `CloudflareOptions` and
  // `BrowserOptions`, so a misspelt key here still fails `astro check`.
};

/**
 * The options both SDKs initialise with.
 *
 * Errors only. `tracesSampleRate: 0` keeps performance monitoring off, and no
 * Replay integration is added on either side — Session Replay would record a
 * visitor's session, which is the opposite of the stance above.
 *
 * Deployed environments only: see {@link sentryReportingEnabled}. The DSN is
 * still passed when reporting is off, so the SDK initialises and behaves
 * identically in every other respect — one code path, not two.
 *
 * @param environmentName The configured environment name, if any.
 */
export function sentryInitOptions(environmentName?: string | null) {
  return {
    dsn: SENTRY_DSN,
    enabled: sentryReportingEnabled(environmentName),
    environment: sentryEnvironment(environmentName),
    tracesSampleRate: 0,
    dataCollection: SENTRY_DATA_COLLECTION,
  };
}
